// THE AI-FIRST SCORECARD — what one onboarding run proved about a repo, one
// graded item per checklist row (epic #3756, one-way door 2).
//
// LATEST RUN WINS. Stored as `repositories.ai_first_score` (jsonb, one per repo)
// and replaced WHOLE by a newer run's scorecard — newer by its own `at`, never by
// arrival, so a redelivered event from a superseded run cannot walk it back.
// Nothing merges two runs' items, and there is no history column; `run_id` and
// `at` say which run the stored value is. A re-run that grades an item worse
// REPLACES the better grade: the scorecard is what is true of the repo now, not a
// high-water mark.
//
// Also rides the `onboarding_verdict` status event as `scorecard`
// (`value-objects/task-run-event/onboarding.ts`) — the box computes it, the
// mothership's sink stores it — so it is untrusted box input on the way in, and
// every member is bounded and every vocabulary closed.
//
// THE ONE INVARIANT IN THE SHAPE: an `ok` item carries at least one evidence
// entry. "ok" with nothing behind it is the claim this object exists to stop a
// PR body making, so it is unrepresentable rather than discouraged. The other
// three states may carry none: a `gap` is often "nothing exists", and the
// excerpt behind an `unprovable` rides the verdict's `proof.unprovable`.

import { z } from 'zod';

/** The stored shape's version. A literal, so a v2 row is refused by this reader rather than misread. */
export const AI_FIRST_SCORE_VERSION = 1;

/**
 * The graded items, closed, as stable snake ids. The first seven are the keys of
 * the setup checklist (`SETUP_CHECKLIST`, `apps/runner/src/modes/setup/verdict.ts`
 * — domain imports no app, so they are restated and pinned by
 * `ai-first-score.test.ts`); the rest are what the onboarding mode adds.
 *
 * A ONE-WAY DOOR: these ids are keys in stored jsonb. Renaming one makes every
 * stored scorecard unreadable. Adding one makes every stored v1 row fail the
 * exhaustive record below, so an addition is `version: 2` — and, since the
 * latest run wins, the next onboarding run of each repo replaces its row.
 */
export const AiFirstItemId = {
  Policy: 'policy',
  Brain: 'brain',
  Roster: 'roster',
  Skills: 'skills',
  Scripts: 'scripts',
  Wiring: 'wiring',
  Commands: 'commands',
  /** Every credential and connection the work needs, asked for once: landed vs asked, by NAME. */
  Access: 'access',
  /** The house repository layout: laid out, or filed as migration tasks for an old project. */
  Layout: 'layout',
  SmokeTest: 'smoke_test',
  EnvExample: 'env_example',
  AgentsMd: 'agents_md',
  PerWorkspaceBrains: 'per_workspace_brains',
  DocsReadme: 'docs_readme',
  /** `bin/setup` then `bin/check` ran green on the box, at base and at head. */
  GateProved: 'gate_proved',
} as const;
export const AiFirstItemIdSchema = z.enum(AiFirstItemId);
export type AiFirstItemId = z.infer<typeof AiFirstItemIdSchema>;
/** Every item id, in declaration order. */
export const AI_FIRST_ITEM_IDS: readonly AiFirstItemId[] = AiFirstItemIdSchema.options;

/** One item's grade. Closed: a fifth state is a stored-shape change. */
export const AiFirstItemState = {
  /** Present and proved — and so it MUST carry evidence. */
  Ok: 'ok',
  /** Missing or incomplete; the run could not or did not establish it. */
  Gap: 'gap',
  /** A human or a gate declined it (an access ask refused, an artifact adjudicated out). */
  Refused: 'refused',
  /** The run tried to prove it and hit a named failure — see the verdict's `proof.unprovable`. */
  Unprovable: 'unprovable',
} as const;
export const AiFirstItemStateSchema = z.enum(AiFirstItemState);
export type AiFirstItemState = z.infer<typeof AiFirstItemStateSchema>;

/**
 * Evidence entries per item, and characters per entry. Evidence is a POINTER — a
 * path, a command with its exit, a count — never a transcript: the excerpt of a
 * failed command rides the verdict's `proof`, bounded separately. Bounded
 * because this is box-authored input stored on `repositories`: fifteen items at
 * these bounds is at most ~36 KB, and nothing in it is allowed to be unbounded.
 */
export const AI_FIRST_EVIDENCE_MAX_ENTRIES = 8;
export const AI_FIRST_EVIDENCE_MAX_CHARS = 300;

export const AiFirstItemSchema = z
  .object({
    state: AiFirstItemStateSchema,
    evidence: z
      .array(z.string().trim().min(1).max(AI_FIRST_EVIDENCE_MAX_CHARS))
      .max(AI_FIRST_EVIDENCE_MAX_ENTRIES),
  })
  .superRefine((item, ctx) => {
    if (item.state === AiFirstItemState.Ok && item.evidence.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message:
          'an `ok` item must carry at least one evidence entry — ok with nothing behind it is a claim, not a grade',
        path: ['evidence'],
      });
    }
  });
export type AiFirstItem = z.infer<typeof AiFirstItemSchema>;

/**
 * A scorecard, as stored in `repositories.ai_first_score` and as emitted on
 * `onboarding_verdict`. `items` is EXHAUSTIVE over {@link AiFirstItemId}: a
 * scorecard that omits an item has not graded it, and "not graded" is not a
 * state. An unknown key is refused too.
 */
export const AiFirstScoreSchema = z.object({
  version: z.literal(AI_FIRST_SCORE_VERSION),
  /** The onboarding run that computed it (`fleet.evt.<run_id>`) — which run won. */
  run_id: z.string().min(1).max(200),
  /** When that run computed it, ISO-8601. */
  at: z.string().datetime(),
  items: z.record(AiFirstItemIdSchema, AiFirstItemSchema),
});
export type AiFirstScore = z.infer<typeof AiFirstScoreSchema>;
